import $ from 'jquery';

import ProductUpdateCard from 'chairisher/view/product/update/card';

import { updateAuctionPriorityStatus } from 'chairisher/component/auction';
import { toggleComputedSelectCustomInput } from 'chairisher/component/form/fieldcomputed';
import { isAddressAutocompleteEnabled } from 'chairisher/context/google';
import {
    getMetaObject,
    getProductObject,
    getSaleDiscountText,
    getSalePriceText,
    // TODO (CHAIR-19428): Remove feature-flag gating after Seller Managed In-Home / Parcel split rollout.
    isSellerManagedInHomeAndParcelEnabled,
} from 'chairisher/context/listingflow';
import { countryCodes } from 'chairisher/context/location';
import { getProductObjectValueByKey } from 'chairisher/context/product';
import { syncListAriaHidden } from 'chairisher/util/accessibility';
import { formatCurrency } from 'chairisher/util/format';
import { bindSuggestionButton } from 'chairisher/util/form';
import { handleAsyncError } from 'chairisher/util/logging';

/**
 * @param {string|undefined} shippingTypeCode
 * @returns {boolean}
 */
function isSellerDelegatedShippingType(shippingTypeCode) {
    return shippingTypeCode === 'P' || shippingTypeCode === 'S';
}

/**
 * View that encapsulates functionality for the "Logistics" section of the Product listing flow.
 */
class LogisticsCard extends ProductUpdateCard {
    constructor() {
        super('#js-logistics-fields');
        /**
         * Mapping of ui-only field name to real field name to use for disabling fields
         *
         * @type {Object.<string, string>}
         */
        this.uiOnlyFieldNameToRealFieldNameMap = {
            does_fit_in_box: 'shipping_type_code',
            is_shipping_free: 'seller_delegated_shipping_charge',
            local_delivery_available: 'seller_local_delivery_distance',
            should_chairish_handle_shipping: 'shipping_type_code',
        };

        /**
         * Used to traverse the DOM to select the card's completion indicator
         *
         * @type {string}
         */
        this.completionIndicatorSelector = '.js-step-logistics';

        /**
         * List of names and selectors for the form groups that contain default price values
         *
         * @type {Array.<Object>}
         */
        this.defaultPriceFormGroups = [
            { name: 'reserve_price_pct_off', selector: '.js-reserve-price-pct-off' },
            { name: 'auction_reserve_price_pct_off', selector: '.js-auction-reserve-price-pct-off' },
            { name: 'trade_discount_percent', selector: '.js-trade-discount-percent' },
        ];

        // TODO (CHAIR-19428): Remove after Seller Managed In-Home / Parcel split rollout.
        this.isSellerManagedInHomeAndParcelEnabled = isSellerManagedInHomeAndParcelEnabled();

        super.initialize();
    }

    /**
     * Selects dropdown option that matches existing or default value, or renders custom field
     *
     * @param {Object} productData The product data to check for existing value
     */
    setPriceSelectOptions(productData) {
        this.defaultPriceFormGroups.forEach(({ name, selector }) => {
            const $formGroup = $(selector);
            const $select = $formGroup.find('.js-computed-select');
            const $input = $formGroup.find(`[name=${name}]`);

            let formValue;
            const existingValue = productData[name]?.value;
            if (Number.isInteger(existingValue)) {
                formValue = existingValue;
            } else if (getMetaObject().id) {
                formValue = '';
            } else {
                formValue = $input.val();
            }

            if ($select.find('option').is(`[value="${formValue}"]`)) {
                $select.val(formValue);
            } else {
                $select.find('option:last').prop('selected', true);
                $select.addClass('computed-select-custom');
                $formGroup.find('.js-computed-input-group').removeClass('hidden');
            }

            $select.prop('disabled', $input.is(':disabled'));
        });
    }

    /**
     * Updates the current values and visibility of this card's simple fields with a given set of data.
     *
     * @param {Object} data The data to update the fields with
     */
    updateFieldsFromDataObject(data) {
        // The pickup state can be a select element or a free-form text input.  If it should be the former, change the
        // element type so the parent method can append the appropriate choices.
        const pickupStateDatum = data.pickup_state;
        let $pickupStateEl = $('[name="pickup_state"]');
        let hasPickupStateElTypeChanged = false;
        const havePickupStateChoices = pickupStateDatum.choices || pickupStateDatum.choices_url;
        if ($pickupStateEl.is('input') && havePickupStateChoices) {
            const selectAttrs = {};
            $.each($pickupStateEl[0].attributes, (_, attribute) => {
                if (attribute.nodeName !== 'type') {
                    selectAttrs[attribute.nodeName] = attribute.nodeValue;
                }
            });
            $pickupStateEl.replaceWith($('<select>', selectAttrs));
            hasPickupStateElTypeChanged = true;
        }

        // clear shipping type code errors
        const $shippingAndPackingFields = $('.js-shipping-and-packing');
        $shippingAndPackingFields.find('.js-errors').html('');
        $shippingAndPackingFields.find('.js-form-group.has-error').removeClass('has-error');

        super.updateFieldsFromDataObject(data);

        // change element to input
        if ($pickupStateEl.is('select') && !havePickupStateChoices) {
            const inputAttrs = { type: 'text' };
            $.each($pickupStateEl[0].attributes, (_, attribute) => {
                inputAttrs[attribute.nodeName] = attribute.nodeValue;
            });
            $pickupStateEl.replaceWith($('<input>', inputAttrs));
            hasPickupStateElTypeChanged = true;
        }

        if (hasPickupStateElTypeChanged) {
            $pickupStateEl = $('[name="pickup_state"]'); // reassign element now that the element has changed
            $pickupStateEl.on('change', () => super.sanitizeAndTriggerSimpleFieldChange($pickupStateEl));
        }

        const $card = this.$cardEl;
        const $isLocalPickupAvailable = $('.js-is-local-pickup-available');
        const product = getProductObject();
        const meta = getMetaObject();
        if (product?.pickup_country_code?.value || meta.is_pamono) {
            const $arrangedShippingRadioButtonDescription = $('#id_should_chairish_handle_shipping_1')
                .parents('.boolean-list-item-inner-container')
                .find('.js-choice-desc');
            const $arrangedShippingUSOnlyMessage = $('.js-shipping-usa-only-message');
            const hideDeliveryFields = product.pickup_country_code.value !== countryCodes.us || meta.is_pamono;
            $arrangedShippingUSOnlyMessage.toggleClass('hidden', hideDeliveryFields);
            $arrangedShippingRadioButtonDescription.toggleClass('hidden', hideDeliveryFields);
            $isLocalPickupAvailable.toggleClass('hidden', hideDeliveryFields);
            $('.js-delivery-fields').toggleClass('hidden', hideDeliveryFields);
            if (hideDeliveryFields) {
                $isLocalPickupAvailable.find('[name=is_local_pickup_available]:checked').prop('checked', false);
            }
        }

        const metaObject = this.getMetaObject();
        if (metaObject.is_jewelry) {
            $isLocalPickupAvailable.find('[name=is_local_pickup_available]:checked').prop('checked', false);
            $isLocalPickupAvailable.addClass('hidden');

            const $isLocalDeliveryAvailable = $('.js-local-delivery-distance');
            $isLocalDeliveryAvailable.find('[name=local_delivery_available]:checked').prop('checked', false);
            $isLocalDeliveryAvailable.addClass('hidden');

            $('.js-delivery-fields').addClass('hidden');
        }

        const $sellerArrangedShippingInput = $card.find('[name=should_chairish_handle_shipping][value="S"]');
        const $sellerArrangedShippingChoice = $sellerArrangedShippingInput.closest('.js-boolean-list-item');
        if (product.shipping_type_code.choices.some((choice) => isSellerDelegatedShippingType(choice.value))) {
            $sellerArrangedShippingChoice.removeClass('hidden');
        } else {
            $sellerArrangedShippingInput.prop('checked', false);
            $sellerArrangedShippingChoice.addClass('hidden');
        }

        $.each(this.uiOnlyFieldNameToRealFieldNameMap, (uiOnlyFieldName, realFieldName) => {
            $card.find(`[name="${uiOnlyFieldName}"]`).prop('disabled', !!product[realFieldName]?.is_readonly);
        });

        //
        // Seller Delegated Shipping
        //
        const $sellerDelegatedShippingCharge = $('[name=seller_delegated_shipping_charge]');
        const $isShippingFree = $('[name="is_shipping_free"]');
        if (parseInt($sellerDelegatedShippingCharge.val(), 10) === 0 && !$isShippingFree.is(':checked')) {
            $isShippingFree.filter('[value="0"]').prop('checked', true);
        }
        if ($isShippingFree.filter('[value=0]:checked').length) {
            $sellerDelegatedShippingCharge.val('');
        }

        //
        // Shipping type code
        //
        const shippingTypeCode = product.shipping_type_code?.value;
        if (shippingTypeCode) {
            if (this.isSellerManagedInHomeAndParcelEnabled && isSellerDelegatedShippingType(shippingTypeCode)) {
                $sellerArrangedShippingInput.prop('checked', true);
                const sizeValue = shippingTypeCode === 'P' ? '0' : 'W';
                $card.find(`[name=does_fit_in_box][value="${sizeValue}"]`).prop('checked', true);
            } else {
                this.getShippingTypeCodeEl(shippingTypeCode).prop('checked', true);
            }
        }

        //
        // Shipping type suggestion
        //
        const suggestionClassName = 'suggested';

        $(`.${suggestionClassName}`).removeClass(suggestionClassName);

        const suggestion = product.shipping_type_code?.suggestion?.value;
        const isDropAndGoSuggested = suggestion === 'A';
        const isDropShipSuggested = suggestion === 'D';
        const isFlatRateSuggested = suggestion === 'F';
        const isSellerParcelSuggested = suggestion === 'P';
        const isSellerInHomeSuggested = suggestion === 'S';
        const isWhiteGloveSuggested = suggestion === 'W';

        const $chairishArrangesShippingChoice = $card
            .find('[name=should_chairish_handle_shipping][value="0"]')
            .closest('.js-boolean-list-item');
        const $doesFitInBoxChoice = $card.find('[name=does_fit_in_box][value="0"]').closest('.js-boolean-list-item');
        const $dropAndGoChoice = $card.find('[name=shipping_type_code][value="A"]').closest('.js-shipping-choice');
        const $dropShippingChoice = $card
            .find('[name=should_chairish_handle_shipping][value="D"]')
            .closest('.js-boolean-list-item');
        const $flatRateChoice = $card.find('[name=shipping_type_code][value="F"]').closest('.js-shipping-choice');
        const $tooBigForBoxChoice = $card.find('[name=does_fit_in_box][value="W"]').closest('.js-boolean-list-item');

        $chairishArrangesShippingChoice.toggleClass(
            suggestionClassName,
            isDropAndGoSuggested || isFlatRateSuggested || isWhiteGloveSuggested,
        );
        $sellerArrangedShippingChoice.toggleClass(
            suggestionClassName,
            isSellerParcelSuggested || isSellerInHomeSuggested,
        );
        $doesFitInBoxChoice.toggleClass(
            suggestionClassName,
            isDropAndGoSuggested || isFlatRateSuggested || isSellerParcelSuggested,
        );
        $dropAndGoChoice.toggleClass(suggestionClassName, isDropAndGoSuggested);
        $dropShippingChoice.toggleClass(suggestionClassName, isDropShipSuggested);
        $flatRateChoice.toggleClass(suggestionClassName, isFlatRateSuggested);
        $tooBigForBoxChoice.toggleClass(suggestionClassName, isWhiteGloveSuggested || isSellerInHomeSuggested);

        const shippingChoices = product.shipping_type_code?.choices;
        if (shippingChoices) {
            const isChairishArrangedShippingAvailable = shippingChoices.some(
                (choice) => choice.value === 'F' || choice.value === 'W',
            );
            $chairishArrangesShippingChoice.toggleClass('hidden', !isChairishArrangedShippingAvailable);

            //
            // Drop & Go program fee, selection, and disabled status
            //
            const dropAndGoOption = shippingChoices.find((el) => el.value === 'A');
            const $dropAndGoRadio = $card.find('[name=shipping_type_code][value="A"]');

            if (dropAndGoOption) {
                $card.find('#js-drop-and-go-fee').text(dropAndGoOption.display);
            } else {
                $dropAndGoRadio.prop('checked', false);
                $dropAndGoRadio.closest('.js-shipping-choice').toggleClass('selected', false);
            }

            let isDropAndGoDisabled = !dropAndGoOption;
            if (product.shipping_type_code?.is_readonly) {
                isDropAndGoDisabled = true;
            }

            $dropAndGoRadio.prop('disabled', isDropAndGoDisabled);
            $dropAndGoRadio.closest('.js-shipping-choice').toggleClass('disabled', isDropAndGoDisabled);
        }

        // update to consume all the changes...
        this.updateShippingCards(shippingTypeCode);

        //
        // Pickup Postal Code
        //

        $('.js-zip').text($('[name=pickup_postal_code]').val());

        //
        // Shipping availability hints and tips
        //
        const isShippableByChairish = metaObject.is_shippable_by_chairish;
        $card
            .find(
                [
                    '.js-international-pickup-tip',
                    '.js-international-seller-delegated-details',
                    '.js-international-shipping-help-text',
                    '.js-international-shipping-tip',
                ].join(','),
            )
            .toggleClass('hidden', isShippableByChairish);
        $card
            .find(
                [
                    '.js-local-delivery-distance',
                    '#js-local-delivery-options',
                    '.js-pickup-tip',
                    '.js-seller-delegated-details',
                    '.js-shipping-help-text',
                    '.js-shipping-tip',
                ].join(','),
            )
            .toggleClass('hidden', !isShippableByChairish);

        //
        // Local delivery
        //

        // delivery distance
        const $deliveryDistance = $card.find('[name=seller_local_delivery_distance]');
        const deliveryDistance = product.seller_local_delivery_distance;
        const hasDeliveryDistance = deliveryDistance && deliveryDistance.value !== undefined && isShippableByChairish;

        const $localDeliveryCheckbox = $card.find('.js-local-delivery');

        // delivery charge
        const $deliveryCharge = $card.find('[name=seller_local_delivery_charge]');
        const deliveryCharge = product.seller_local_delivery_charge;
        const hasDeliveryCharge = deliveryCharge && deliveryCharge.value !== undefined && isShippableByChairish;

        if (hasDeliveryDistance) {
            $localDeliveryCheckbox.prop('checked', true);
        }

        $deliveryDistance.prop('disabled', !hasDeliveryDistance && !hasDeliveryCharge);

        $deliveryCharge.prop('disabled', !hasDeliveryCharge);

        const $localDeliveryChargeCheckbox = $card.find('.js-local-delivery-charge');
        $localDeliveryChargeCheckbox.prop('checked', hasDeliveryCharge);

        const $deliveryOptions = $card.find('#js-local-delivery-options');

        if (!hasDeliveryCharge && !hasDeliveryDistance) {
            $deliveryDistance.val('');
        } else if (!$deliveryOptions.hasClass('in')) {
            $card.find('#js-local-delivery-options').collapse('show');
        }

        //
        // Price Visual
        //

        [
            { name: 'price', selector: '.js-price-visual-header-price', shouldClearOnError: true },
            { name: 'price', selector: '.js-price-visual-chart-label-max', shouldClearOnError: true },
            { name: 'reserve_price_pct_off', selector: '.js-price-visual-header-discount', shouldClearOnError: false },
        ].forEach((field) => {
            const fieldDisplay = product[field.name]?.display;
            const shouldClear = this.shouldClearComputedPriceFields && field.shouldClearOnError;
            $(field.selector).text(fieldDisplay && !shouldClear ? fieldDisplay : 'TBD');
        });

        //
        // Auction Reserve Price
        //

        updateAuctionPriorityStatus({
            container: $('.js-additional-price-groups')[0],
            discount: product.auction_reserve_price_pct_off?.value,
        });

        // toggles arp fields based on quantity
        const isMultiple = product.num_lots_available?.value > 1;
        $('.js-auction-reserve-price-pct-off, .js-priority-status-section').toggleClass('hidden', isMultiple);

        //
        // Sales
        //
        const { sales } = getMetaObject();
        const $sales = [];
        if (Array.isArray(sales) && sales.length) {
            for (let i = 0; i < sales.length; i += 1) {
                const sale = sales[i];
                const saleText = sale.is_shop ? getSaleDiscountText(sale.pct_off) : getSalePriceText(sale.sale_price);

                const html =
                    `${sale.start_date} - ${sale.end_date}` +
                    `<span class="scheduled-sales-discount"> ${saleText}</span>`;

                $sales.push($('<li></li>').html(html));
            }
        }
        const $salesDatesList = $('#js-sales-dates');
        $salesDatesList.html($sales);
        syncListAriaHidden($salesDatesList[0]);
        $('#js-sales').toggleClass('hidden', !$sales.length);
    }

    /** @inheritdoc */
    updateInstructionsFromDataObject(data) {
        $.each(
            [
                {
                    fieldName: 'num_lots_available',
                    instructionsSelector: '.js-num-lots-available-instructions',
                },
                {
                    fieldName: 'shipping_height',
                    instructionsSelector: '.js-shipping-dimensions-instructions',
                },
            ],
            (_, obj) => {
                const key = obj.fieldName;
                const selector = obj.instructionsSelector;
                const datum = data[key];
                const instructions = datum && datum.instructions ? datum.instructions : [];
                this.getCardEl()
                    .find(selector)
                    .text(instructions.map((k) => k.display).join(' '));
            },
        );
    }

    /** @inheritdoc */
    updateWarningsFromDataObject(data) {
        //
        // Unit Type
        //
        this.updateFieldWarnings($('.js-unit-type-warnings-wrapper'), data.unit_type.warnings);
    }

    /**
     * Binds actions to the various elements that comprise the Card
     */
    bind() {
        super.bind();

        const $card = this.$cardEl;

        //
        // Computed Form Groups
        //

        const $priceFormGroups = $('.js-additional-price-groups');
        $priceFormGroups.toggleClass('hidden', !getProductObjectValueByKey('price'));

        $('input[name="price"]').on('change', ({ currentTarget }) => {
            const priceValue = $(currentTarget).val();
            if (priceValue) {
                this.defaultPriceFormGroups.forEach(({ name, selector }) => {
                    const $formGroup = $(selector);
                    const $input = $formGroup.find(`[name=${name}]`);
                    if ($formGroup.find('.js-computed-select').val() || $input.val()) {
                        $input.trigger('change');
                    }
                });
            }

            $priceFormGroups.toggleClass('hidden', !priceValue);
        });

        this.defaultPriceFormGroups.forEach(({ name, selector }) => {
            const $formGroup = $card.find(selector);
            const $select = $formGroup.find('.js-computed-select');
            const $input = $formGroup.find('.js-computed-input-group input');

            $select.on('change', ({ currentTarget }) => {
                const selectedValue = parseInt($(currentTarget).val(), 10);

                if (!Number.isNaN(selectedValue)) {
                    $input.val(selectedValue).trigger('change');
                } else {
                    $input.val('');
                    $formGroup.find('.js-computed-field-value').text('');

                    if (name === 'reserve_price_pct_off') {
                        $('.js-price-visual-header-discount').text('TBD');
                    }
                }

                toggleComputedSelectCustomInput({ $select, $input, selectedValue });
            });

            bindSuggestionButton({ $formGroup, $input, $select });
        });

        //
        // Is Shipping Free? / Seller Delegated Shipping Charge
        //

        $card.on('change', '[name=should_chairish_handle_shipping]', (e) => {
            let val = e.target.value;
            const changeObj = {
                packing_charge: null,
            };
            const isArrangeOwnShipping = val === 'S';

            if (val === '0' || (this.isSellerManagedInHomeAndParcelEnabled && isArrangeOwnShipping)) {
                val = null;
            }

            if (!isSellerDelegatedShippingType(val)) {
                changeObj.seller_delegated_shipping_charge = null;
            }

            changeObj.shipping_type_code = val;

            // uncheck the "what size is this item" checkboxes
            $card.find('[name=does_fit_in_box]').prop('checked', false);
            $card.find('[name=shipping_type_code]').prop('checked', false);

            this.updateShippingCards(val);
            this.triggerChange(changeObj);
        });

        $card.on('change', '[name=is_shipping_free]', (e) => {
            let val = parseInt(e.target.value, 10);
            const $sellerDelegatedShippingCharge = this.$cardEl.find('[name=seller_delegated_shipping_charge]');

            if (val === 0) {
                $sellerDelegatedShippingCharge.val('');
            } else {
                if (val) {
                    $sellerDelegatedShippingCharge.focus();
                }

                // initially we need the remove the current value set so we can overwrite it with
                val = null;
            }

            this.updateShippingCards();
            this.triggerChange({ seller_delegated_shipping_charge: val });
        });

        //
        // What size is your item
        //

        $card.on('change', '[name=does_fit_in_box]', (e) => {
            let val = e.target.value;
            const changeObj = {
                packing_charge: null,
            };
            const isArrangeOwnShipping = $card
                .find('[name=should_chairish_handle_shipping][value="S"]')
                .prop('checked');

            if (this.isSellerManagedInHomeAndParcelEnabled && isArrangeOwnShipping) {
                val = val === '0' ? 'P' : 'S';
            } else if (val === '0') {
                val = null;
            }

            changeObj.shipping_type_code = val;

            this.updateShippingCards(val);
            this.triggerChange(changeObj);
        });

        //
        // Google Autocomplete Address
        //
        if (isAddressAutocompleteEnabled()) {
            import('chairisher/component/autocomplete/google/address').then(
                ({ default: GoogleAutocompleteAddress }) => {
                    const googleAutocompleteAddress = new GoogleAutocompleteAddress({
                        $addressFields: $('.js-address-fields'),
                        hasSingleStreetLine: true,
                        isPickupAddress: true,
                    });
                    googleAutocompleteAddress.bind();
                },
                handleAsyncError,
            );
        }

        //
        // Local Delivery
        //

        $('[name=pickup_postal_code]').on('change', ({ currentTarget }) => {
            $('.js-zip').text($(currentTarget).val());
        });

        $('.js-local-delivery').on('change', ({ currentTarget }) => {
            const isChecked = $(currentTarget).is(':checked');
            const $el = $('[name=seller_local_delivery_distance]');

            $el.prop('disabled', !isChecked);
            $('#js-local-delivery-options').collapse(isChecked ? 'show' : 'hide');

            if (!isChecked) {
                $el.val('').trigger('change');
                $('.js-local-delivery-charge').prop('checked', false);
                $('[name=seller_local_delivery_charge]').val('').trigger('change');
            }
        });

        $('.js-local-delivery-charge').on('change', ({ currentTarget }) => {
            const isChecked = $(currentTarget).is(':checked');
            const $el = $('[name=seller_local_delivery_charge]');

            $el.prop('disabled', !isChecked);

            if (!isChecked) {
                $el.val('').trigger('change');
            }
        });

        $('.js-shipping-choice').on('click', (e) => {
            const $target = $(e.currentTarget).find('input');
            if (!$target.is(':disabled')) {
                $target.prop('checked', true).trigger('change');
            }
        });

        $('.js-shipping-options, .js-shipping-size-options, .js-free-shipping').on('click', (e) => {
            const $closestInput = $(e.target).closest('.js-boolean-list-item').find('input');

            if (!$closestInput.is(':disabled') && !$closestInput.prop('checked')) {
                $closestInput.prop('checked', true).trigger('change');
            }
        });

        $('[name=shipping_type_code]').on('click', (e) => {
            e.stopPropagation(); // don't bubble to .js-shipping-choice

            if ($(e.target).is(':disabled')) {
                return;
            }

            // cache off the old value to see if packing charge changes after calling updateShippingCards()...
            const $packingCharge = this.$cardEl.find('[name=packing_charge]');
            const oldValue = $packingCharge.val();

            this.updateShippingCards(e.target.value);

            const newValue = $packingCharge.val();
            if (newValue !== oldValue) {
                $packingCharge.trigger('change');
            }
        });

        // List item help text
        $card.find('.js-boolean-list-help-text').on('click', (e) => {
            e.preventDefault();

            $(e.currentTarget).closest('.js-boolean-list-item').find('label').trigger('click');
        });
    }

    /**
     * The easiest way to get the right jQuery element representing a given shipping _type_code because
     * shipping_type_code was split into multiple checkboxes in different sections...
     *
     * @param {string} shippingTypeCode The shipping_type_code to use to find the jQuery element
     * @returns {jQuery}
     */
    getShippingTypeCodeEl(shippingTypeCode) {
        let $stc;
        const $card = this.$cardEl;
        if (shippingTypeCode) {
            $stc = $card.find(`[name=shipping_type_code][value="${shippingTypeCode}"]`);

            if ($stc.length === 0) {
                $stc = $card.find(`[name=should_chairish_handle_shipping][value="${shippingTypeCode}"]`);
            }

            if ($stc.length === 0) {
                $stc = $card.find(`[name=does_fit_in_box][value="${shippingTypeCode}"]`);
            }
        } else {
            $stc = $card.find('[name=should_chairish_handle_shipping]:checked');

            if ($stc.length === 0 || !$stc.val()) {
                $stc = $card.find('[name=shipping_type_code]:checked');
            }

            if ($stc.length === 0 || !$stc.val()) {
                $stc = $card.find('[name=does_fit_in_box]:checked');
            }
        }

        return $stc;
    }

    /**
     * Resolves the effective shipping_type_code from an explicit value or the current UI selection hierarchy.
     *
     * @param {string=} shippingTypeCode Optional shipping_type_code from a change handler / product load
     * @returns {string|undefined}
     */
    resolveSelectedShippingTypeCode(shippingTypeCode) {
        let selectedShippingTypeCode = shippingTypeCode;
        const $card = this.$cardEl;
        const packingTypeCode = $card.find('[name=shipping_type_code]:checked').val();
        const shouldChairishHandleShipping = $card.find('[name=should_chairish_handle_shipping]:checked').val();
        const sizeValue = $card.find('[name=does_fit_in_box]:checked').val();

        if (!selectedShippingTypeCode) {
            if (shouldChairishHandleShipping === 'D') {
                selectedShippingTypeCode = 'D';
            } else if (shouldChairishHandleShipping === 'S') {
                if (!this.isSellerManagedInHomeAndParcelEnabled) {
                    selectedShippingTypeCode = 'S';
                } else if (sizeValue === '0') {
                    selectedShippingTypeCode = 'P';
                } else if (sizeValue === 'W') {
                    selectedShippingTypeCode = 'S';
                }
            } else if (packingTypeCode) {
                selectedShippingTypeCode = packingTypeCode;
            } else if (shouldChairishHandleShipping === '0' && sizeValue === 'W') {
                selectedShippingTypeCode = 'W';
            }
        }

        return selectedShippingTypeCode;
    }

    populateSimpleFieldNames() {
        this.simpleFieldNames = $.makeArray(
            $(this.fieldNames).not(Object.keys(this.uiOnlyFieldNameToRealFieldNameMap)),
        );
    }

    /**
     * Updates the current values and visibility of this card's fields that display meta data.
     *
     * @param {Object} metaData The data to update the fields with
     */
    updateMetaFieldsFromDataObject(metaData) {
        const metaFields = [
            { idSelector: '#js-price-proceeds', name: 'listing_proceeds', shouldHideIfEmpty: true },
            { idSelector: '#js-reserve-price', name: 'reserve_price', shouldHideIfEmpty: false },
            { idSelector: '#js-reserve-proceeds', name: 'reserve_proceeds', shouldHideIfEmpty: false },
            { idSelector: '#js-auction-reserve-price', name: 'auction_reserve_price', shouldHideIfEmpty: false },
            { idSelector: '#js-auction-reserve-proceeds', name: 'auction_reserve_proceeds', shouldHideIfEmpty: false },
            { idSelector: '#js-net-price', name: 'trade_price', shouldHideIfEmpty: false },
            { idSelector: '#js-net-proceeds', name: 'trade_proceeds', shouldHideIfEmpty: false },
        ];

        metaFields.forEach((field) => {
            const $field = $(field.idSelector);
            const responseValue = metaData[field.name];
            const value = responseValue && !this.shouldClearComputedPriceFields ? responseValue : '';

            const $computedFieldValue = $field.find('.js-computed-field-value');
            if (field.name === 'auction_reserve_proceeds') {
                // The ARP proceeds field is a range instead of a single value
                const askingPrice = metaData.asking_price;
                const range = value && askingPrice ? `${formatCurrency(value)} - ${formatCurrency(askingPrice)}+` : '';
                $computedFieldValue.text(range);
            } else {
                $computedFieldValue.text(value ? formatCurrency(value) : '');
            }

            $field.toggleClass('hidden', value === '' && field.shouldHideIfEmpty);
        });

        this.shouldClearComputedPriceFields = false;
    }

    /**
     * Updates the errors displayed for a given field
     *
     * @param {string} fieldName The name of the field to display errors for
     * @param {Array.<Choice>} fieldErrors The errors to display
     */
    updateFieldErrors(fieldName, fieldErrors) {
        if (fieldName === 'shipping_type_code') {
            ['should_chairish_handle_shipping', 'does_fit_in_box', 'shipping_type_code'].forEach((name) =>
                super.updateFieldErrors(name, fieldErrors),
            );
        } else if (fieldName === 'seller_delegated_shipping_charge') {
            [
                'should_chairish_handle_shipping',
                'is_shipping_free',
                'seller_delegated_shipping_charge',
            ].forEach((name) => super.updateFieldErrors(name, fieldErrors));
        } else if (['price', 'reserve_price_pct_off', 'trade_discount_percent'].includes(fieldName)) {
            this.shouldClearComputedPriceFields = true;
            super.updateFieldErrors(fieldName, fieldErrors);
        } else {
            super.updateFieldErrors(fieldName, fieldErrors);
        }
    }

    /**
     * Updates the shipping card checkboxes, disabled properties, and visibility based on the current state of the ui
     *
     * @param {string=} shippingTypeCode Optional shipping_type_code to use to find the jQuery element
     */
    updateShippingCards(shippingTypeCode) {
        const $card = this.$cardEl;
        const $doesFitInBox = $card.find('[name=does_fit_in_box]');
        const $dropAndGoChoice = $card.find('[name=shipping_type_code][value="A"]').closest('.js-shipping-choice');
        const $dropShipChoice = $card
            .find('[name=should_chairish_handle_shipping][value="D"]')
            .closest('.js-boolean-list-item');
        const $flatRateChoice = $card.find('[name=shipping_type_code][value="F"]').closest('.js-shipping-choice');
        const $shouldChairishHandleShipping = $card.find('[name=should_chairish_handle_shipping]');
        const isSellerManagedSizeEnabled = this.isSellerManagedInHomeAndParcelEnabled;

        const isDropAndGoDisabled = $dropAndGoChoice.hasClass('disabled');
        const isDropShippingRecommended = $dropShipChoice.hasClass('suggested');
        const isFlatRateRecommended = $flatRateChoice.hasClass('suggested');
        const selectedShippingTypeCode = this.resolveSelectedShippingTypeCode(shippingTypeCode);

        const doesFitInBox = $doesFitInBox.filter('[value="0"]').prop('checked');
        const doesNotFitInBox = $doesFitInBox.filter('[value="W"]').prop('checked');
        const isChairishHandlingShipping = $shouldChairishHandleShipping.filter('[value="0"]').prop('checked');
        const isArrangeOwnShipping = $shouldChairishHandleShipping.filter('[value="S"]').prop('checked');
        const isDropAndGoShipping = selectedShippingTypeCode === 'A';
        const isDropShipping = selectedShippingTypeCode === 'D';
        const isFlatRateShipping = selectedShippingTypeCode === 'F';
        const isNoShippingSelected = selectedShippingTypeCode === undefined || selectedShippingTypeCode === '0';
        const isSellerParcelShipping = selectedShippingTypeCode === 'P';
        const isSellerDelegatedShipping = isSellerDelegatedShippingType(selectedShippingTypeCode);
        const isWhiteGlove = selectedShippingTypeCode === 'W';
        const usesSellerManagedSizeOptions =
            isSellerManagedSizeEnabled && (isArrangeOwnShipping || isSellerDelegatedShipping);

        //
        // Determine which steps in the process to display. Top level are "Have Chairish Arrange Shipping" vs "Arrange
        // My Own Shipping" vs (sometimes) "Dropship on Chairish's Account".  Second level for Chairish-Arranged is
        // "Fits into a box" vs "Too big to fit into a box".  Third level for fits in a box is "Chairish Drop Off & Go"
        // vs "Pack the item myself".
        // With the Parcel split, Arrange My Own also uses the size question to choose Parcel vs In-Home.
        //

        // display the box sizing questions for Chairish-arranged shipping, or Arrange My Own when the split is enabled
        const shouldDisplaySizeOption = !!(
            isChairishHandlingShipping ||
            isDropAndGoShipping ||
            isFlatRateShipping ||
            isWhiteGlove ||
            usesSellerManagedSizeOptions
        );
        $('#js-size-options').collapse(shouldDisplaySizeOption ? 'show' : 'hide');
        $('.js-shipping-internationally-message').toggleClass('hidden', usesSellerManagedSizeOptions);

        // display the packing questions if Chairish is handling shipping and the user has indicated that the item fits
        // in a box
        const shouldDisplayPackingQuestions = !!(
            (isChairishHandlingShipping && doesFitInBox) ||
            isDropAndGoShipping ||
            isFlatRateShipping
        );
        $('#js-your-options').collapse(shouldDisplayPackingQuestions ? 'show' : 'hide');

        // display the in-home delivery options only if the user has indicated it's too large to fit in a box
        // (Chairish-arranged path only)
        const shouldDisplayWhiteGloveOptions = !!((isChairishHandlingShipping && doesNotFitInBox) || isWhiteGlove);
        $('#js-in-home-delivery-options').collapse(shouldDisplayWhiteGloveOptions ? 'show' : 'hide');

        // display seller delegated shipping options only if the user has chosen Parcel or In-Home
        $('#js-seller-delegated-shipping-options').collapse(isSellerDelegatedShipping ? 'show' : 'hide');

        // display the drop shipping options only if the user has chosen drop shipping or such is recommended
        const shouldDisplayDropShipOptions = !!(isDropShipping || (isNoShippingSelected && isDropShippingRecommended));
        $('#js-drop-shipping-options').collapse(shouldDisplayDropShipOptions ? 'show' : 'hide');

        // display full text for Drop & Go unless another option is selected or recommended
        const $dropAndGoMaxText = $dropAndGoChoice.find('.js-maximized-text');
        const $dropAndGoMinText = $dropAndGoChoice.find('.js-minimized-text');
        if (!isDropAndGoDisabled && (isDropAndGoShipping || !(isFlatRateShipping || isFlatRateRecommended))) {
            $dropAndGoMinText.hide();
            $dropAndGoMaxText.show();
        } else {
            $dropAndGoMinText.show();
            $dropAndGoMaxText.hide();
        }

        // display full text for flat rate shipping only if that option is selected or recommended
        const $flatRateMaxText = $flatRateChoice.find('.js-maximized-text');
        const $flatRateMinText = $flatRateChoice.find('.js-minimized-text');
        if (isFlatRateShipping || isFlatRateRecommended) {
            $flatRateMinText.hide();
            $flatRateMaxText.show();
        } else {
            $flatRateMinText.show();
            $flatRateMaxText.hide();
        }

        //
        // For each step, determine which additional form elements should be present or absent
        //

        // display the various seller delegated shipping options and enable/disable them appropriately
        const $checkedShippingType = $card.find('[name=shipping_type_code]:checked');
        const $sellerDelegatedFreeShippingChoices = $card.find('[name=is_shipping_free]');
        const $sellerDelegatedShippingCharge = $card.find('[name=seller_delegated_shipping_charge]');

        if (isSellerDelegatedShipping) {
            const sellerDelegatedShippingCharge = parseInt($sellerDelegatedShippingCharge.val(), 10);
            const $sellerDelegatedShippingFeeCharged = $sellerDelegatedFreeShippingChoices.filter('[value="1"]');
            const $sellerDelegatedFreeShipping = $sellerDelegatedFreeShippingChoices.filter('[value="0"]');
            const $freeOption = $sellerDelegatedFreeShipping.closest('.js-boolean-list-item');
            const $paidOption = $sellerDelegatedShippingFeeCharged.closest('.js-boolean-list-item');

            if (!$sellerDelegatedFreeShippingChoices.is(':checked')) {
                if (Number.isNaN(sellerDelegatedShippingCharge) || sellerDelegatedShippingCharge === 0) {
                    $sellerDelegatedShippingFeeCharged.prop('checked', false);
                    $sellerDelegatedShippingCharge.val('');
                } else {
                    $sellerDelegatedShippingFeeCharged.prop('checked', true);
                    $sellerDelegatedFreeShipping.prop('checked', false);
                }
            }

            let isSellerDelegatedShippingDisabled = !$sellerDelegatedShippingFeeCharged.is(':checked');
            const data = getProductObject();
            if (data && data.seller_delegated_shipping_charge.is_readonly) {
                isSellerDelegatedShippingDisabled = true;
            }
            $sellerDelegatedShippingCharge.prop('disabled', isSellerDelegatedShippingDisabled);

            // display full text for seller delegated shipping options that are selected
            const $freeOptionText = $freeOption.find('.js-maximized-text');
            const $paidOptionText = $paidOption.find('.js-maximized-text');

            if ($sellerDelegatedFreeShipping.prop('checked')) {
                $freeOptionText.show();
                $paidOptionText.hide();
            } else if ($sellerDelegatedShippingFeeCharged.prop('checked')) {
                $freeOptionText.hide();
                $paidOptionText.show();
            } else {
                $freeOptionText.hide();
                $paidOptionText.hide();
            }

            // uncheck non-seller-delegated options in case the user switches back
            $checkedShippingType.closest('.js-shipping-choice').removeClass('selected');
            $checkedShippingType.prop('checked', false);
        } else {
            // uncheck the seller-delegated options in the case the user switches back
            $sellerDelegatedFreeShippingChoices.prop('checked', false);
            $sellerDelegatedShippingCharge.val('');
            $sellerDelegatedShippingCharge.prop('disabled', true);
        }

        // enable/disable the flat rate packing charge option when appropriate
        const $packingChargeField = $card.find('[name=packing_charge]');

        $packingChargeField.prop('disabled', !isFlatRateShipping);
        if (!isFlatRateShipping) {
            $packingChargeField.val('');
        }

        // backfill higher level options in the selection hierarchy if necessary
        if (isDropShipping) {
            $doesFitInBox.prop('checked', false);
        } else if (isSellerDelegatedShipping) {
            $shouldChairishHandleShipping.filter('[value="S"]').prop('checked', true);

            if (isSellerManagedSizeEnabled) {
                const sizeValue = isSellerParcelShipping ? '0' : 'W';
                $doesFitInBox.filter(`[value="${sizeValue}"]`).prop('checked', true);
            } else {
                $doesFitInBox.prop('checked', false);
            }
        } else if (isDropAndGoShipping || isFlatRateShipping || isWhiteGlove || doesFitInBox || doesNotFitInBox) {
            $shouldChairishHandleShipping.filter('[value="0"]').prop('checked', true);

            if (isDropAndGoShipping || isFlatRateShipping) {
                $doesFitInBox.filter('[value="0"]').prop('checked', true);
            }
        }

        // ensure that the correct options appear selected at each level of the selection hierarchy
        $shouldChairishHandleShipping.closest('.js-boolean-list-item').removeClass('selected');
        $shouldChairishHandleShipping.filter(':checked').closest('.js-boolean-list-item').addClass('selected');
        $doesFitInBox.closest('.js-boolean-list-item').removeClass('selected');
        $doesFitInBox.filter(':checked').closest('.js-boolean-list-item').addClass('selected');
        $('.js-shipping-choice').removeClass('selected');
        $('[name=shipping_type_code]:checked').closest('.js-shipping-choice').addClass('selected');
        $('.js-free-shipping .js-boolean-list-item').removeClass('selected');
        $sellerDelegatedFreeShippingChoices.filter(':checked').closest('.js-boolean-list-item').addClass('selected');
    }
}

export default LogisticsCard;
